import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { Overpull, PreInstallLoadType } from 'src/app/perivis/models/load-case.model';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';

export interface OverpullForm {
  OverpullForce: number;
  IsFlotation: boolean;
  InternalFluidDensity: number;
  HasBottomCap: boolean;
  HasFlotationPlug: boolean;
  PlugDepth: number;
  FluidDensity: number;
  type: PreInstallLoadType;
}

@Component({
    selector: 'app-overpull-details',
    template: `
    <form [formGroup]="form.formGroup" class="space-y-2">

      <div class="flex flex-col pt-2">
        <label>Overpull Force</label>
		    <app-validated-number-input
          [id]="'overpullForce'"
          [control]="control('OverpullForce')"
          [value]="control('OverpullForce').value"
          [validation]="forceValidation"
          [unit]="forceUnit"
          [inline]="false">
        </app-validated-number-input>
      </div>

      <div class="flex flex-col">
        <label>Internal Fluid Density</label>
        <app-validated-number-input
          [id]="'internalFluidDensity'"
          [control]="control('InternalFluidDensity')"
          [value]="control('InternalFluidDensity').value"
          [validation]="densityValidation"
          [unit]="densityUnit"
          [inline]="false">
        </app-validated-number-input>
      </div>

      <div class="flex mt-2">
        <p-fieldset class="w-full">
          <ng-template pTemplate="header">
            <div class="flex align-items-center h-5">
              <p-toggleswitch
                binary="true"
                [formControlName]="form.formControlNames.IsFlotation"
                (onChange)="checkFlotation()"
                inputId="isFlotation"
              ></p-toggleswitch>
              <label class="pl-1" for="isFlotation">Flotation</label>
            </div>
          </ng-template>

        <div class="flex flex-col space-y-2">
          <div>
            <p-checkbox
              size="small"
              binary="true"
              [formControlName]="form.formControlNames.HasBottomCap"
              inputId="btmCap"
            ></p-checkbox>
            <label class="ml-2 pt-1" for="btmCap">Bottom end cap or float plug</label>
          </div>

          <div>
            <p-checkbox
              inputId="float-plug"
              size="small"
              binary="true"
              [formControlName]="form.formControlNames.HasFlotationPlug"
              (change)="hasPlugChange()"
            ></p-checkbox>
            <label for="float-plug" class="ml-2 pt-1">Has Plug</label>
          </div>

          <div class="flex flex-col">
            <label>Plug Depth</label>
              <app-validated-number-input
                [id]="'PlugDepth'"
                [control]="control('PlugDepth')"
                [value]="control('PlugDepth').value"
                [validation]="{ min: hangerMd, max: shoeMd }"
                [unit]="depthUnit"
                [inline]="false">
              </app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>Fluid Density (below plug)</label>
              <app-validated-number-input
                [id]="'FluidDensity'"
                [control]="control('FluidDensity')"
                [value]="control('FluidDensity').value"
                [validation]="densityValidation"
                [unit]="densityUnit"
                [inline]="false">
              </app-validated-number-input>
          </div>
        </div>
      </p-fieldset>
    </div>
    
  </form>
  `,
    providers: subformComponentProviders(OverpullDetailsComponent),
    standalone: false
})
export class OverpullDetailsComponent implements OnInit, AfterViewInit {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  @Input() userUnits: UserUnitsModel;
  @Input() shoeMd: number;
  @Input() hangerMd: number;
  @Input() annularFLuidDensity: number;

  public forceUnit: string;
  public forceValidation: { min: number, max: number };
  public densityUnit: string;
  public densityValidation: { min: number, max: number };
  public depthUnit: string;
  public isFlotation: boolean;
  public hasFlotationPlug: boolean;

  ngOnInit(): void {
    this.forceUnit = unitsLib[this.userUnits.force].symbol;
    this.densityUnit = unitsLib[this.userUnits.density].symbol;
    this.depthUnit = this.userUnits.longLengths;

    switch (this.userUnits.force) {
      case 'lbf':
        this.forceValidation = { min: -100000, max: 10000000 };
        break;
      case 'tf':
        this.forceValidation = { min: -45, max: 4535 };
        break;
      case 'kgf':
        this.forceValidation = { min: -45359, max: 4535923 };
        break;
      case 'kip':
        this.forceValidation = { min: -100, max: 10000 };
        break;
    }
    this.densityValidation = { min: GetValueFromPpg(0.01, this.densityUnit), max: GetValueFromPpg(25, this.densityUnit) };

    this.form.formGroup.controls.OverpullForce.setValidators([Validators.min(this.forceValidation.min), Validators.max(this.forceValidation.max), Validators.required]);
    this.form.formGroup.controls.InternalFluidDensity.setValidators([Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max), Validators.required]);
    this.form.formGroup.controls.FluidDensity.setValidators([Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);
    this.form.formGroup.controls.PlugDepth.setValidators([Validators.min(this.hangerMd), Validators.max(this.shoeMd)]);
  }

  ngAfterViewInit(): void {
    if (!this.form.formGroup.controls.InternalFluidDensity.value) {
      this.form.formGroup.controls.InternalFluidDensity.setValue(this.annularFLuidDensity);
    }

    if (!this.form.formGroup.controls.FluidDensity.value && this.form.formGroup.controls.FluidDensity.value !== 0) {
      if (this.form.formGroup.controls.HasFlotationPlug.value) {
        this.form.formGroup.controls.FluidDensity.setValue(this.annularFLuidDensity);
      } else {
        this.form.formGroup.controls.FluidDensity.setValue(this.annularFLuidDensity, { emitEvent: false });
      }
    }

    if (!this.form.formGroup.controls.PlugDepth.value) {
      if (this.form.formGroup.controls.HasFlotationPlug.value) {
        this.form.formGroup.controls.PlugDepth.setValue(this.shoeMd);
      } else {
        this.form.formGroup.controls.PlugDepth.setValue(this.shoeMd, { emitEvent: false });
      }
    }

    if (this.form.formGroup.controls.HasFlotationPlug.value) {
      this.hasPlug();
    } else {
      this.doesntHavePlug();
    }

    this.checkFlotation();
  }

  public form = createForm<OverpullForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(null, { updateOn: 'blur' }),
      OverpullForce: new UntypedFormControl(null, { updateOn: 'blur' }),
      IsFlotation: new UntypedFormControl(false),
      InternalFluidDensity: new UntypedFormControl(null, { updateOn: 'blur' }),
      HasBottomCap: new UntypedFormControl(false),
      HasFlotationPlug: new UntypedFormControl(false),
      PlugDepth: new UntypedFormControl(null, { updateOn: 'blur' }),
      FluidDensity: new UntypedFormControl(null, { updateOn: 'blur' })
    },
    toFormGroup: (obj: Overpull | null): OverpullForm => {
      if (!obj) {
        return null;
      }

      return {
        OverpullForce: obj.OverpullForce,
        IsFlotation: obj.IsFlotation,
        InternalFluidDensity: obj.InternalFluidDensity,
        HasBottomCap: obj.HasBottomCap,
        HasFlotationPlug: obj.HasFlotationPlug,
        PlugDepth: obj.PlugDepth,
        FluidDensity: obj.FluidDensity,
        type: obj.type
      }
    },
    fromFormGroup: (formValue: OverpullForm): Overpull => {
      return {
        OverpullForce: formValue.OverpullForce,
        IsFlotation: formValue.IsFlotation,
        InternalFluidDensity: formValue.InternalFluidDensity,
        HasBottomCap: formValue.HasBottomCap,
        HasFlotationPlug: formValue.HasFlotationPlug,
        PlugDepth: formValue.PlugDepth ? formValue.PlugDepth : this.shoeMd,
        FluidDensity: formValue.FluidDensity ?? this.annularFLuidDensity,
        type: PreInstallLoadType.OVERPULL
      }
    }
  });

  public checkFlotation() {
    if (this.form.formGroup.controls.IsFlotation.value) {
      this.form.formGroup.controls.HasBottomCap.enable({ emitEvent: false });
      this.form.formGroup.controls.HasFlotationPlug.enable({ emitEvent: false });
      this.hasPlug();
      this.isFlotation = true;
    } else {
      this.form.formGroup.controls.HasBottomCap.disable({ emitEvent: false });
      this.form.formGroup.controls.HasFlotationPlug.disable({ emitEvent: false });
      this.doesntHavePlug();
      this.isFlotation = false;
    }
  }

  public hasPlugChange() {
    if (this.form.formGroup.controls.HasFlotationPlug.value) {
      this.hasPlug();
    } else {
      this.doesntHavePlug();
    }
  }

  private hasPlug() {
    if (!this.form.formGroup.controls.PlugDepth.value) {
      this.form.formGroup.controls.PlugDepth.setValue(this.shoeMd, { emitEvent: false });
    }
    if (this.form.formGroup.controls.HasFlotationPlug.value || !this.form.formGroup.controls.IsFlotation.value) {
      this.form.formGroup.controls.PlugDepth.enable({ emitEvent: false });
      this.form.formGroup.controls.FluidDensity.enable({ emitEvent: false });
      this.hasFlotationPlug = true;
    }
  }

  private doesntHavePlug() {
    if (!this.form.formGroup.controls.HasFlotationPlug.value || !this.form.formGroup.controls.IsFlotation.value) {
      this.form.formGroup.controls.PlugDepth.disable({ emitEvent: false });
      this.form.formGroup.controls.FluidDensity.disable({ emitEvent: false });
      this.hasFlotationPlug = false;
    }
  }
}